<template>
  <v-dialog
    max-width="502"
    :value="show"
    @click:outside="close"
    @keydown.esc="close"
  >
    <v-card class="ma-0 pa-0">
      <v-card-title class="ma-0 px-6 py-4">
        <p
          data-testid="revCompletedDialog-text-title"
          class="h6 text--darken-3 ma-0 pa-0"
        >
          {{ title }}
        </p>
      </v-card-title>

      <v-card-text class="ma-0 px-6 pb-4 pt-1">
        <p
          data-testid="revCompletedDialog-text-description"
          class="body-2 ma-0 pa-0"
        >
          {{ subtitle }}
        </p>

        <p
          v-if="isCoachAssigned && !hasImplementPermission"
          data-testid="revCompletedDialog-text-description"
          class="body-2 ma-0 pa-0 mt-4"
        >
          {{ $t('common.roles.coach_one') }}:
        </p>

        <AssigneesSearchPopover
          v-if="!hasImplementPermission"
          allowEmptySelection
          :disabled="isCoachAssigned"
          :selectedAccountIds="[coachId]"
          :blacklist="coachBlacklist"
          :filters="{ isCoach: true }"
          @change="handleSelectCoach"
        >
          <ChipAvatar
            v-if="isCoachAssigned"
            :account="coach"
            :vuetifyProps="{
              close: false,
              ripple: false,
            }"
          />

          <v-btn
            v-else
            rounded
            class="blue-grey--text text--darken-1 body-2 mt-6"
            color="blue-grey lighten-3"
            elevation="0"
          >
            <v-icon size="16" class="mr-1" color="blue-grey darken-1">
              mdi-plus
            </v-icon>
            <p class="ma-0 pa-0 blue-grey--text text--darken-1 body-2">
              {{ $t('common.button.select_coach') }}
            </p>
          </v-btn>
        </AssigneesSearchPopover>

        <v-btn
          v-else
          data-tourid="implement-button"
          class="pa-4 ma-0 mt-4"
          rounded
          color="primary"
          @click="$emit('implementRev')"
          elevation="0"
        >
          <v-icon color="white" left>mdi-rocket-launch</v-icon>
          <p class="ma-0 pa-0 white--text body-2">
            {{ implementText }}
          </p>
        </v-btn>

        <p
          data-testid="revCompletedDialog-text-description"
          class="ma-0 pa-0 body-2 mt-4"
        >
          {{ $t('rev_cycle.review.select_coach_description') }}
        </p>
      </v-card-text>
      <v-divider class="ma-0 pa-0" />
      <v-card-actions class="d-flex justify-end ma-0 px-6 py-4">
        <Button
          plain
          class="mr-4"
          color="grey"
          data-testid="revCompletedDialog-button-close"
          :text="$t('common.button.close')"
          @click="close"
        />

        <Button
          plain
          color="primary"
          iconPosition="right"
          icon="mdi-arrow-right"
          data-testid="revCompletedDialog-button-go-to-impact"
          :text="$t('common.button.go_to_impact')"
          @click="$emit('goToReview')"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash'

import Button from '@/components/UI/Button'

import AssigneesSearchPopover from '@/components/AssigneesSearchPopover/AssigneesSearchPopover.vue'

import ChipAvatar from '@/components/ChipAvatar.vue'
import getEntityId from '@/lib/getEntityId'

export default {
  name: 'RevCompletedModal',

  components: {
    Button,
    AssigneesSearchPopover,
    ChipAvatar,
  },

  props: {
    show: {
      type: Boolean,
      required: true,
    },
    revTeam: Object,
    hasImplementPermission: {
      type: Boolean,
      required: false,
    },
    organizationId: String,
    isImplemented: Boolean,
  },

  computed: {
    implementText() {
      if (!this.isImplemented) {
        return this.$t('rev.mark_as_implemented_button')
      }

      return this.$t('rev.unmark_as_implemented_button')
    },
    title() {
      return this.hasImplementPermission
        ? this.$t('rev_cycle.review.great_job')
        : this.$t('rev_cycle.review.great_progress')
    },

    subtitle() {
      if (this.hasImplementPermission)
        return this.$t('rev_cycle.review.complete_implement_action')

      return this.isCoachAssigned
        ? this.$t('rev_cycle.review.complete_next_action')
        : this.$t('rev_cycle.review.select_coach_action')
    },

    owner() {
      return this.revTeam?.owner ?? {}
    },

    collaborators() {
      return this.revTeam.collaborators
    },

    coachBlacklist() {
      return _.compact([
        this.owner?._id,
        ...(this.collaborators?.map(collaborator => collaborator._id) ?? []),
      ])
    },

    coach() {
      if (_.isEmpty(this.revTeam?.coach)) return null
      return this.revTeam?.coach
    },

    coachId() {
      if (!this.coach) return null
      return getEntityId(this.coach)
    },

    isCoachAssigned() {
      return Boolean(this.coachId)
    },
  },

  methods: {
    close() {
      this.$emit('close')
    },

    handleSelectCoach(selectedUser) {
      this.$emit('setCoach', selectedUser.items?.[0] ?? null)
    },
  },
}
</script>
